<template>
  <div>
    <app-nav />
    <div class="container-fluid">
      <div class="columns">
        <div class="column">
          <span class="title">{{ app.name }}</span>
          <button
            class="button primary create-update-btn"
            @click="openModal('create')"
            v-if="hasNoWebhook && !isOnlySpectator()"
          >
            {{ $t('webhooks.info.create_webhook') }}
          </button>
          <button
            class="button primary create-update-btn"
            @click="openModal('update')"
            :disabled="validationStatus['requested'] === 'in_progress'"
            v-else-if="!isOnlySpectator()"
          >
            {{ $t('webhooks.info.update_webhook') }}
          </button>
        </div>
      </div>
      <div>
        <div class="columns">
          <div class="column is-4" v-if="activeWebhook">
            <webhook-card
              title="Active webhook"
              type="active"
              :app="app"
              :webhook="activeWebhook"
              :is-only-spectator="isOnlySpectator()"
              @show-details="showDetails"
              @update-success="getActiveWebhook"
            />
          </div>
          <div class="column is-8" v-if="activeWebhook">
            <error-logs :errors="errors" />
          </div>
        </div>
        <div class="columns">
          <div class="column is-4" v-if="requestedWebhook">
            <webhook-card
              title="Requested webhook"
              type="requested"
              :app="app"
              :webhook="requestedWebhook"
              :is-only-spectator="isOnlySpectator()"
              @update-success="getRequestedWebhook"
              @request-success="getRequestedWebhookStatus"
              @show-details="showDetails"
            />
          </div>
          <div class="column is-4" v-if="!(requestedWebhook || activeWebhook)">
            <webhook-card :app="app" title="Webhook" />
          </div>
        </div>
      </div>
    </div>
    <webhook-config-upsert-modal
      :is-active="configUpsertModal.isActive"
      :modal-title="configUpsertModal.title"
      @close-modal="closeUpsertModal"
      @request-success="getRequestedWebhookStatus"
    ></webhook-config-upsert-modal>

    <webhook-config-view-modal
      :is-active="configViewModal.isActive"
      :modal-title="configViewModal.title"
      :webhook="webhookDetails"
      @close-modal="closeViewModal"
    ></webhook-config-view-modal>
  </div>
</template>

<script>
import store from 'store';
import { mapGetters, mapState } from 'vuex';
import i18n from 'services/i18n';

import ErrorLogs from './modules/ErrorLogs';
import notifications from 'services/Notifications';
import AppNav from 'views/boards/common/modules/AppNav.vue';
import WebhookConfigUpsertModal from './modules/WebhookConfigUpsertModal';
import WebhookConfigViewModal from './modules/WebhookConfigViewModal';
import { WebhookCard } from 'components/cards';

export default {
  name: 'WebhookBoard',
  beforeRouteEnter(to, from, next) {
    store
      .dispatch('application/loadApp', to.params.appId)
      .then(() => {
        return store.commit('webhook/resetState');
      })
      .then(() => {
        return store.commit('webhook/setCurrentId', to.params.appId);
      })
      .then(() => {
        return store.dispatch('webhook/getWebhooksForAppId', to.params.appId);
      })
      .then(next)
      .catch(err => {
        notifications.add('Unable to get webhook data. Please try again');
      });
  },
  components: {
    AppNav,
    ErrorLogs,
    WebhookCard,
    WebhookConfigUpsertModal,
    WebhookConfigViewModal,
  },
  computed: {
    ...mapState('webhook', [
      'currentWebhookId',
      'activeWebhook',
      'requestedWebhook',
      'errors',
    ]),
    ...mapState('account', ['appsByAccount']),
    ...mapGetters('application', ['app']),
    ...mapGetters('webhook', ['validationStatus']),
    hasNoWebhook() {
      return !(this.activeWebhook || this.requestedWebhook);
    },
  },
  data() {
    return {
      webhookDetails: null,
      configUpsertModal: {
        isActive: false,
        title: 'Create Request',
      },
      configViewModal: {
        isActive: false,
        title: 'Request Details',
      },
    };
  },
  async mounted() {
    await this.checkAndGetRecentActions();

    if (this.activeWebhook) {
      await this.loadErrors();
    }

    await this.getCustomMessageTypes();
  },
  methods: {
    async loadErrors() {
      try {
        await store.dispatch('webhook/getErrors');
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.getting_errors_failed'));
      }
    },

    async getCustomMessageTypes() {
      try {
        await store.dispatch('webhook/getCustomMessageTypes');
      } catch (err) {
        console.error(err);
        notifications.add(
          this.$t('webhooks.errors.getting_custom_message_types_failed')
        );
      }
    },

    showDetails(webhook) {
      this.webhookDetails = webhook;
      this.configViewModal.isActive = true;
    },

    closeUpsertModal() {
      this.configUpsertModal.isActive = false;
    },

    closeViewModal() {
      this.configViewModal.isActive = false;
    },

    async checkAndGetRecentActions() {
      try {
        const status = this.validationStatus;

        if (
          status.requested === 'failure' ||
          (this.requestedWebhook && this.requestedWebhook.is_approved === false)
        ) {
          await store.dispatch(
            'webhook/getWebhooksRecentActions',
            this.requestedWebhook.id
          );
        }
      } catch (err) {
        console.error(err);
        notifications.add(
          this.$t('webhooks.errors.getting_verification_logs_failed')
        );
      }
    },

    openModal(type) {
      store.commit('webhook/resetRequestFormErrors');
      store.commit('webhook/updateRequestFormState');

      this.configUpsertModal.title =
        type === 'update' ? 'Update Request' : 'Create Request';
      this.configUpsertModal.isActive = true;
    },

    async getActiveWebhook() {
      try {
        await store.dispatch('webhook/getActiveWebhook');
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.getting_status_failed'));
      }
    },

    async getRequestedWebhook() {
      try {
        await store.dispatch('webhook/getRequestedWebhook');
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.getting_status_failed'));
      }
    },

    async getRequestedWebhookStatus() {
      try {
        await this.getRequestedWebhook();

        if (this.validationStatus.requested === 'in_progress') {
          setTimeout(() => {
            this.getRequestedWebhookStatus();
          }, 5000);
        }
      } catch (err) {
        console.error(err);
        notifications.add(this.$t('webhooks.errors.getting_status_failed'));
      }
    },
    isOnlySpectator() {
      for (const [key, value] of Object.entries(this.appsByAccount)) {
        // Should not include the developer role for this to be a truthy value
        if (
          key === this.app.account_id &&
          !value.roles.includes('developer') &&
          value.roles.includes('spectator')
        ) {
          return true;
        }
      }
      return false;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
.container-fluid {
  padding: 2rem;
}
</style>
